import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Card, Form, Tab, Nav, Alert, Button } from 'react-bootstrap';
import { RefreshCw, Repeat } from 'lucide-react';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';

import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../hooks';
import { customerAnalyticsAPI, categoryAPI } from '../../services';
import CampaignEvaluationTab from '../../components/customer-analytics/CampaignEvaluationTab';
import RepeatCustomersTab from '../../components/customer-analytics/RepeatCustomersTab';
import LoyalCohortTab from '../../components/customer-analytics/LoyalCohortTab';

import RepurchaseSummaryCards from '../../components/customer-analytics/RepurchaseSummaryCards';
import ProductRepurchaseTable from '../../components/customer-analytics/ProductRepurchaseTable';
import ProductLoyalCustomersPanel from '../../components/customer-analytics/ProductLoyalCustomersPanel';
import CustomerHistoryModal from '../../components/customer-analytics/CustomerHistoryModal';
import TopLoyalCustomersTable from '../../components/customer-analytics/TopLoyalCustomersTable';
import StaffContributionTable from '../../components/customer-analytics/StaffContributionTable';

const LOYAL_THRESHOLDS = [2, 3, 4, 5, 10];

export default function CustomerAnalyticsPage() {
  const { selectedBranch } = useBranch();

  const [fromDate, setFromDate] = useState('2026-01-01');
  const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
  const [categoryId, setCategoryId] = useState('');
  const [loyalThreshold, setLoyalThreshold] = useState(3);
  const [sort, setSort] = useState('repurchase_rate');
  const [activeTab, setActiveTab] = useState('products');

  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [products, setProducts] = useState(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [staff, setStaff] = useState(null);
  const [staffLoading, setStaffLoading] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCustomerPanel, setShowCustomerPanel] = useState(false);
  const [historyPhone, setHistoryPhone] = useState(null);

  const queryParams = useMemo(() => {
    const params = {
      from_date: fromDate,
      to_date: toDate,
      loyal_threshold: loyalThreshold,
    };
    if (selectedBranch?.id) params.branch_id = selectedBranch.id;
    if (categoryId) params.category_id = categoryId;
    return params;
  }, [fromDate, toDate, loyalThreshold, selectedBranch, categoryId]);

  // Tab "Khách quay lại" chỉ dùng khoảng ngày + chi nhánh — bỏ danh mục/ngưỡng loyal của tab món,
  // để đổi 2 filter kia không nạp lại bảng vô ích.
  const repeatQueryParams = useMemo(() => {
    const params = { from_date: fromDate, to_date: toDate };
    if (selectedBranch?.id) params.branch_id = selectedBranch.id;
    return params;
  }, [fromDate, toDate, selectedBranch]);

  // Load categories once
  useEffect(() => {
    categoryAPI
      .getCategories()
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.data || [];
        setCategories(list);
      })
      .catch((err) => console.error('[CustomerAnalytics] load categories', err));
  }, []);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await customerAnalyticsAPI.getSummary(queryParams);
      setSummary(res);
    } catch (err) {
      console.error(err);
      toast.error('Không tải được tổng quan');
    } finally {
      setSummaryLoading(false);
    }
  }, [queryParams]);

  const fetchProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const res = await customerAnalyticsAPI.getProductRepurchase({
        ...queryParams,
        sort,
        limit: 200,
        min_purchase_count: 1,
      });
      setProducts(res);
    } catch (err) {
      console.error(err);
      toast.error('Không tải được bảng xếp hạng món');
    } finally {
      setProductsLoading(false);
    }
  }, [queryParams, sort]);

  const fetchStaff = useCallback(async () => {
    setStaffLoading(true);
    try {
      const res = await customerAnalyticsAPI.getStaffContribution(queryParams);
      setStaff(res);
    } catch (err) {
      console.error(err);
      toast.error('Không tải được báo cáo nhân viên');
    } finally {
      setStaffLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchSummary();
    fetchProducts();
  }, [fetchSummary, fetchProducts]);

  useEffect(() => {
    if (activeTab === 'staff') fetchStaff();
  }, [activeTab, fetchStaff]);

  const handleRefresh = () => {
    fetchSummary();
    fetchProducts();
    if (activeTab === 'staff') fetchStaff();
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setShowCustomerPanel(true);
  };

  const handleShowCustomerHistory = (phone) => {
    setHistoryPhone(phone);
  };

  return (
    <PageLayout>
      <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h3 className="mb-1">
            <Repeat size={22} className="me-2" />
            Phân tích khách mua lại
          </h3>
          <div className="text-muted small">
            Tỉ lệ khách quay lại mua theo món (dựa trên SĐT) — chỉ xét đơn đã <strong>COMPLETED</strong>.
            Dùng để cải thiện menu và làm cơ sở chia thưởng nhân viên.
          </div>
        </div>
        <Button variant="outline-primary" size="sm" onClick={handleRefresh} disabled={summaryLoading || productsLoading}>
          <RefreshCw size={14} className={`me-1 ${summaryLoading || productsLoading ? 'spin' : ''}`} />
          Làm mới
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-3 shadow-sm">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Label className="small text-muted mb-1">Danh mục</Form.Label>
              <Form.Select
                size="sm"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <option value="">Tất cả danh mục</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small text-muted mb-1">
                Ngưỡng khách loyal
              </Form.Label>
              <Form.Select
                size="sm"
                value={loyalThreshold}
                onChange={(e) => setLoyalThreshold(Number(e.target.value))}
              >
                {LOYAL_THRESHOLDS.map((t) => (
                  <option key={t} value={t}>≥ {t} lần mua</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="small text-muted mb-1">Từ ngày</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={fromDate}
                max={toDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Label className="small text-muted mb-1">Đến ngày</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={toDate}
                min={fromDate}
                max={moment().format('YYYY-MM-DD')}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Summary cards */}
      <RepurchaseSummaryCards summary={summary} loading={summaryLoading} />

      {/* Top 5 insight alert */}
      {summary?.top_repurchase_products?.length > 0 && (
        <Alert variant="info" className="mb-3">
          <strong>Top 5 món có khách quay lại nhiều nhất:</strong>{' '}
          {summary.top_repurchase_products.map((p, i) => (
            <span key={p.menu_item_id}>
              {i > 0 && ' · '}
              <strong>{p.menu_item_name}</strong> ({(p.repurchase_rate * 100).toFixed(0)}%, {p.total_customers} khách)
            </span>
          ))}
        </Alert>
      )}

      {/* Tabs */}
      <Card className="shadow-sm">
        <Card.Body>
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
            <Nav variant="tabs" className="mb-3">
              <Nav.Item>
                <Nav.Link eventKey="products">Xếp hạng món</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="loyal">Khách hàng loyal</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="staff">Đóng góp nhân viên</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="campaign">Đánh giá chiến dịch</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="repeat">Khách quay lại theo chi nhánh</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="loyal-cohort">Nhóm khách quen</Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              <Tab.Pane eventKey="products">
                <ProductRepurchaseTable
                  data={products}
                  loading={productsLoading}
                  sort={sort}
                  onSortChange={setSort}
                  onSelectProduct={handleSelectProduct}
                  selectedProductId={selectedProduct?.menu_item_id}
                />
              </Tab.Pane>

              <Tab.Pane eventKey="loyal">
                <TopLoyalCustomersTable
                  productsData={products}
                  loyalThreshold={loyalThreshold}
                  loading={productsLoading}
                  onShowCustomerHistory={handleShowCustomerHistory}
                  onLoadMoreForProduct={handleSelectProduct}
                />
              </Tab.Pane>

              <Tab.Pane eventKey="staff">
                <StaffContributionTable data={staff} loading={staffLoading} />
              </Tab.Pane>

              <Tab.Pane eventKey="campaign">
                <CampaignEvaluationTab queryParams={queryParams} />
              </Tab.Pane>

              <Tab.Pane eventKey="repeat">
                {activeTab === 'repeat' && (
                  <RepeatCustomersTab queryParams={repeatQueryParams} />
                )}
              </Tab.Pane>

              {/* Tab nặng (tính trên toàn bộ lịch sử + gộp nhóm), chỉ nạp khi thực sự mở. */}
              <Tab.Pane eventKey="loyal-cohort">
                {activeTab === 'loyal-cohort' && (
                  <LoyalCohortTab queryParams={repeatQueryParams} />
                )}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>

      <ProductLoyalCustomersPanel
        show={showCustomerPanel}
        onHide={() => setShowCustomerPanel(false)}
        product={selectedProduct}
        queryParams={queryParams}
        onShowCustomerHistory={handleShowCustomerHistory}
      />

      <CustomerHistoryModal
        show={!!historyPhone}
        onHide={() => setHistoryPhone(null)}
        phone={historyPhone}
        queryParams={queryParams}
      />

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </PageLayout>
  );
}
